import DOMPurify from "dompurify";
import clsx from "clsx";
import styles from "./styles.module.scss";
import React from "react";
import { marked } from "marked";
import { trackLinkClick, linkifyEmailsAndPhonesInHtml } from "../../../../../libs/fns";

const Model = React.forwardRef<
  HTMLDivElement,
  { className?: string; model: string }
>(({ className, model }, ref) => {
  // 何も表示するものがない場合はレンダリングしない
  if (!model || !model.trim()) {
    return null;
  }

  // カスタムレンダラーを設定してリンクに自動でtarget="_blank"を追加
  const renderer = new marked.Renderer();
  renderer.link = ({ href, title, tokens }) => {
    const rawText = tokens.map(token => token.raw || '').join('');
    const isUrl = /^https?:\/\//i.test(rawText.trim());
    const isTooLong = rawText.length > 30;
    const text = (isUrl || isTooLong) ? '詳細はこちら' : rawText;
    const titleAttr = title ? ` title="${title}"` : '';
    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
  };

  const markedOptions = {
    breaks: true,  // 単一改行を<br>に変換（ブロック要素間の不要な<br>はnormalizeBrTagsで除去）
    gfm: true,
    headerIds: false,
    mangle: false,
    renderer: renderer,
    pedantic: false,  // 厳密なマークダウン解析を無効化（誤検出を防ぐ）
  };

  const normalizeLineBreaks = (text: string): string => {
    return text
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t\u3000\u00a0]+$/gm, '')
      .replace(/^[ \t\u3000\u00a0]+/gm, '');
  };

  // HTML内の連続<br>タグを1つに統一する関数
  const normalizeBrTags = (html: string): string => {
    return html
      // 連続する<br>タグ（間に空白があってもなくても）を1つに統一
      .replace(/(<br\s*\/?>[\s\n]*)+/gi, '<br>')
      // ブロック要素の直前の<br>を削除（例: </p><br><ol> → </p><ol>）
      .replace(/<br>\s*(<\/?(p|div|ol|ul|li|h[1-6])[^>]*>)/gi, '$1')
      // ブロック要素の直後の<br>を削除（例: </ol><br><p> → </ol><p>）
      .replace(/(<\/?(p|div|ol|ul|li|h[1-6])[^>]*>)\s*<br>/gi, '$1')
      // 空の段落タグを削除
      .replace(/<p[^>]*>\s*<\/p>/gi, '');
  };

  const normalizedModel = normalizeLineBreaks(model);

  // リンククリックハンドラー（useCallbackで安定化）
  const handleLinkClick = React.useCallback((event: MouseEvent) => {
    // クリックされた実際のアンカー要素を取得（targetではなくcurrentTargetを利用）
    const anchor = event.currentTarget as HTMLAnchorElement | null;
    const href = anchor?.getAttribute('href');

    if (!href) return;

    // iOS / iPadOS / Safari をより堅牢に判定
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
    const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);

    // Safari系またはモバイル環境ではデフォルト動作を活かす（非同期処理でユーザーアクションを失わない）
    const isNarrowViewport = window.innerWidth < 768;
    const treatAsMobileSafari = isIOS || (isSafari && (isNarrowViewport || 'ontouchstart' in window));

    if (treatAsMobileSafari) {
      // 追跡はsendBeaconで非同期送信（ナビゲーションをブロックしない）
      // 存在チェックのみ（副作用なし）
      const hasSendBeacon = 'sendBeacon' in navigator;

      // sendBeacon があれば利用、なければ非同期で投げるだけ
      try {
        if (hasSendBeacon) {
          // NOTE: 実際のエンドポイント送信は trackLinkClick 内で実施しているため
          // ここでは競合を避け、trackLinkClick を fire & forget で呼ぶに留める
          // iOS Safari では preventDefault を行わず、デフォルトの遷移に任せる
          trackLinkClick(href).catch(() => { });
        } else {
          // フォールバック: 非同期で呼ぶ（await しない）
          trackLinkClick(href).catch(() => { });
        }
      } catch { }
      // preventDefault はしない → a[target="_blank"] のデフォルトで新規タブが開く
      return;
    }

    // デスクトップやその他ブラウザ: ナビゲーションを制御（同期に window.open）
    // ユーザーアクション内で即時に window.open を実行し、追跡はブロックしない
    event.preventDefault();
    try {
      window.open(href, '_blank', 'noopener,noreferrer');
    } finally {
      // 追跡は後段で非同期実行（結果は待たない）
      trackLinkClick(href).catch(() => { });
    }
  }, []);

  // リンクにイベントリスナーを設定する関数
  const setupLinks = React.useCallback(() => {
    if (!ref || typeof ref === 'function' || !ref.current) return;

    const element = ref.current;
    const links = element.querySelectorAll('a[href]');

    links.forEach((link) => {
      const el = link as HTMLElement;
      if (el.dataset.otaiInit === '1') return;
      link.addEventListener('click', handleLinkClick as any);
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      el.dataset.otaiInit = '1';
    });
  }, [ref, handleLinkClick]);

  // 初回とmodelが変更されたときにリンクを設定
  React.useEffect(() => {
    setupLinks();
  }, [model, setupLinks]);

  // MutationObserverでDOMの変更を監視してリンクを再設定
  // ただし、頻繁な更新を避けるためにdebounceを使用
  React.useEffect(() => {
    if (!ref || typeof ref === 'function' || !ref.current) return;

    const element = ref.current;
    let timeoutId: NodeJS.Timeout | null = null;

    // MutationObserverを作成
    const observer = new MutationObserver(() => {
      // 既存のタイムアウトをクリア
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // 300ms後にリンクを再設定（debounce）
      timeoutId = setTimeout(() => {
        setupLinks();
      }, 300);
    });

    // 監視を開始
    observer.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      observer.disconnect();
    };
  }, [ref, setupLinks]);

  // DOMPurifyの設定: target="_blank"とrel属性を許可
  const domPurifyConfig = {
    ADD_ATTR: ['target', 'rel'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class', 'id'],
  };

  // 1) Markdown -> HTML 2) 一旦サニタイズ 3) メール/電話をリンク化 4) <br>正規化 5) 最終サニタイズ
  const firstPass = DOMPurify.sanitize(marked.parse(normalizedModel, markedOptions) as string, domPurifyConfig);
  const linked = linkifyEmailsAndPhonesInHtml(firstPass);
  const brNormalized = normalizeBrTags(linked);
  const htmlContent = DOMPurify.sanitize(brNormalized, domPurifyConfig);

  return (
    <div
      className={clsx(styles.model, className)}
      ref={ref}
      dangerouslySetInnerHTML={{
        __html: htmlContent,
      }}
    />
  );
});

export default Model;
